import { toast } from 'react-toastify';
import translationFallback from './translations/translationFallback';
import { TFunction } from 'i18next';
import { dismissToast } from './toast-utils';
import changeLocalClient from './change-local-client';
import { NextRouter } from 'next/router';
import isStringDefined from './is-string-defined';
import { QueryClient } from '@tanstack/react-query';
import hasAuthToken from './has-auth-token';
import languageConstants from '@/constants/language.constants';

const handleLanguageChange = async (
  language: string,
  tCommon: TFunction,
  router: NextRouter,
  isUserAuth: boolean,
  queryClient: QueryClient | null
): Promise<void> => {
  if (!isStringDefined(language)) {
    toast.error(
      translationFallback(
        'something_went_wrong',
        tCommon('something_went_wrong'),
        'Something went wrong'
      )
    );
    return;
  }

  const languageExist = Object.keys(languageConstants.localesWithIcons).find(
    (key) => key.toLowerCase() === language.toLowerCase()
  );
  const languageKey = languageExist ?? 'en';
  const loadingMessage =
    languageConstants.changingLanguageMessage[
      languageKey as keyof typeof languageConstants.changingLanguageMessage
    ];

  const toastId = toast.loading(loadingMessage);

  try {
    if (isUserAuth) {
      const res = await fetch('/api/change-language', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ language: language.toUpperCase() }),
      });

      if (!res.ok) {
        throw new Error('Failed to change language on server');
      }

      await queryClient?.invalidateQueries({
        queryKey: ['user_v2'],
      });
    }

    await changeLocalClient(language, router);

    toast.success(
      translationFallback(
        'language_changed_successfully',
        tCommon('language_changed_successfully'),
        'Language changed successfully'
      )
    );
  } catch (error: unknown) {
    console.error(error);
    toast.error(
      translationFallback(
        'something_went_wrong',
        tCommon('something_went_wrong'),
        'Something went wrong'
      )
    );
  } finally {
    await dismissToast(toastId);
  }
};

export default handleLanguageChange;
